// components/FormularSesizare/steps/Step2DetaliiSesizare.jsx

import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import roRO from "date-fns/locale/ro";

// Soft UI Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

import CharacterCounter from "../CharacterCounter";
import {
  FIELD_LIMITS,
  createSanitizedChangeHandler,
  sanitizeDescriere,
  sanitizeOras,
  sanitizeSubiect,
  sanitizeTipuriVatamare,
} from "../validation";

// Listă județe România
const judeteRomania = [
  "Alba", "Arad", "Argeș", "Bacău", "Bihor", "Bistrița-Năsăud", "Botoșani", "Brașov", 
  "Brăila", "București", "Buzău", "Caraș-Severin", "Călărași", "Cluj", "Constanța", 
  "Covasna", "Dâmbovița", "Dolj", "Galați", "Giurgiu", "Gorj", "Harghita", "Hunedoara", 
  "Ialomița", "Iași", "Ilfov", "Maramureș", "Mehedinți", "Mureș", "Neamț", "Olt", 
  "Prahova", "Satu Mare", "Sălaj", "Sibiu", "Suceava", "Teleorman", "Timiș", "Tulcea", 
  "Vaslui", "Vâlcea", "Vrancea"
];

// Abbreviation definitions
const abbreviationDefinitions = {
  "MAI": "Ministerul Afacerilor Interne",
  "MPPICJ": "Ministerul Public - Parchetul de pe lângă Înalta Curte de Casație și Justiție",
  "MP-PÎCCJ": "Ministerul Public - Parchetul de pe lângă Înalta Curte de Casație și Justiție",
  "CNCD": "Consiliul Național pentru Combaterea Discriminării",
  "AvP": "Avocatul Poporului"
};

// Denumirea actualizată în UI; valoarea din API poate rămâne „MPPICJ”.
const displayInstitutionLabel = (label) => (label === "MPPICJ" ? "MP-PÎCCJ" : label);

// FRAZE EXPLICATIVE pentru tipul incidentului
// Structura urmează exact documentul „Platforma A.6.1": titlu cu marcator „i" / „ii"
// (roșu, superscript, bold) și body cu paragrafe explicative în italic, eventual cu
// liste sau pasaje care au prefix „i"/„ii".
const frazeExplicative = [
  {
    id: "amenintare",
    title: [
      { text: "Amenințarea (inclusiv online) " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Fapta de a amenința o persoană cu săvârșirea unei infracțiuni sau a unei fapte păgubitoare îndreptate împotriva sa ori a altei persoane, dacă este de natură să îi producă o stare de temere.",
      },
    ],
  },
  {
    id: "lovirea",
    title: [
      { text: "Lovirea sau alte violențe " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic: "Lovirea sau orice acte de violență cauzatoare de suferințe fizice.",
      },
    ],
  },
  {
    id: "distrugerea",
    title: [
      { text: "Distrugerea " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Distrugerea, degradarea sau aducerea în stare de neîntrebuințare a unui bun aparținând altuia ori împiedicarea luării măsurilor de conservare sau de salvare a unui astfel de bun, precum și înlăturarea măsurilor luate.",
      },
      {
        italic:
          "Distrugerea unui înscris sub semnătură privată, care aparține în tot sau în parte altei persoane și servește la dovedirea unui drept de natură patrimonială, dacă prin aceasta s-a produs o pagubă.",
      },
      {
        italic:
          "Distrugerea, degradarea sau aducerea în stare de neîntrebuințare a unui bun, săvârșită prin incendiere, explozie ori prin orice alt asemenea mijloc și dacă este de natură să pună în pericol alte persoane sau bunuri.",
      },
    ],
  },
  {
    id: "incitare",
    title: [
      { text: "Incitarea la violență, ură sau discriminare " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Mesaje care incită, promovează, răspândesc sau justifică violența, ura sau discriminarea împotriva unei persoane sau a unui grup de persoane sau care le denigrează, din cauza unor prejudecăți legate de caracteristicile sau statutul lor personal real sau atribuit.",
      },
    ],
  },
  {
    id: "discriminare",
    title: [
      { text: "Discriminarea pe bază de rasă " },
      { mark: "i" },
      { text: ", naționalitate, etnie sau religie " },
      { mark: "ii" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Teoriile rasiale au fost invalidate științific. Oamenii aparțin aceleiași specii, nu există «rase» diferite. Însă prejudecățile rasiale (rasimul antirom) sunt încă răspândite și se află la baza multor incidente motivate de ură. Din acest motiv, criteriul «rasei» este păstrat în legislație, pentru a proteja persoanele vizate de ura provenită din atribuirea unei anumite «rase».",
      },
      {
        mark: "ii",
        italic:
          "Orice deosebire, excludere, restricție sau preferință pe bază de rasă, naționalitate, etnie sau religie, care are ca scop sau efect restrângerea, înlăturarea recunoașterii, folosinței sau exercitării, în condiții de egalitate, a drepturilor omului și a libertăților fundamentale sau a drepturilor recunoscute de lege, în domeniul politic, economic, social și cultural sau în orice alte domenii ale vieții publice.",
      },
    ],
  },
  {
    id: "confectionare",
    title: [
      { text: "Confecționarea, vânzarea (...) " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Drapelele, emblemele, insignele, uniformele, sloganurile, formulele de salut, precum și orice alte asemenea însemne, care promovează ideile, concepțiile sau doctrinele fasciste, legionare, rasiste antirome, antisemite sau xenofobe.",
      },
    ],
  },
  {
    id: "distribuire",
    title: [
      { text: "Distribuirea ori utilizarea (...) " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Imagini, mesaje text, conținut audio-video, cărți, articole, alte documente și materiale de propagandă, precum și alte asemenea reprezentări, care transmit idei, concepții sau doctrine fasciste, legionare, rasiste antirome, antisemite, ori xenofobe.",
      },
    ],
  },
  {
    id: "promovare-idei",
    title: [
      { text: "Promovarea, în public, a ideilor, concepțiilor sau doctrinelor antisemite, antirome, fasciste, legionare, rasiste sau xenofobe " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Precum ura și violența pe motive etnice, rasiale sau religioase, superioritatea unor rase și inferioritatea altora, incitarea la xenofobie, recurgerea la violență pentru schimbarea ordinii constituționale sau a instituțiilor democratice, naționalismul extremist.",
      },
    ],
  },
  {
    id: "promovare-cult-vinovati",
    title: [
      { text: "Promovarea, în public, a cultului persoanelor vinovate de săvârșirea unor infracțiuni de genocid, contra umanității și de război " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Orice persoană condamnată definitiv de către o instanță judecătorească română ori străină sau prin orice hotărâre recunoscută în România, potrivit legii, pentru una sau mai multe infracțiuni de genocid, contra umanității și de război, precum și persoana din conducerea unei organizații al cărei caracter criminal a fost constatat prin hotărârea unei instanțe penale internaționale.",
      },
    ],
  },
  {
    id: "promovare-cult-conducere",
    title: [
      { text: "Promovarea, în public, a cultului persoanelor care au făcut parte din conducerea organizațiilor fasciste, legionare, rasiste sau xenofobe " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic:
          "Orice grup format din trei sau mai multe persoane, care își desfășoară activitatea temporar sau permanent, în scopul promovării ideilor, concepțiilor sau doctrinelor fasciste, legionare, rasiste sau xenofobe, precum ura și violența pe motive etnice, rasiale sau religioase, superioritatea unor rase și inferioritatea altora, incitarea la xenofobie, recurgerea la violență pentru schimbarea ordinii constituționale sau a instituțiilor democratice, naționalismul extremist. În această categorie pot fi incluse organizațiile cu sau fără personalitate juridică, partidele și mișcările politice, asociațiile și fundațiile, societățile reglementate de Legea societăților nr. 31/1990, republicată, cu modificările și completările ulterioare.",
      },
      {
        italicSegments: [
          { bold: true, text: "Prin organizații legionare se face referire la Mișcarea Legionară:\u00a0" },
          { text: "O organizație fascistă din România care a activat în perioada 1927-1941 sub denumirile de «Legiunea Arhanghelul Mihail», «Garda de Fier» și «Partidul Totul pentru Țară»." },
        ],
      },
    ],
  },
  {
    id: "negare-holocaust",
    title: [
      { text: "Negarea/minimalizarea Holocaustului, inclusiv pe teritoriul României " },
      { mark: "i" },
    ],
    body: [
      {
        mark: "i",
        italic: "Formă specifică de antisemitism care se manifestă de regulă prin:",
      },
      {
        list: [
          "eforturi intenționate de a scuza sau minimaliza impactul Holocaustului sau al elementelor sale principale, inclusiv colaboratori și aliați ai Germaniei naziste;",
          "minimalizarea masivă a numărului victimelor Holocaustului, în contradicție cu informațiile ce provin din surse credibile;",
          "încercări de a susține că evreii și-au cauzat propriul genocid;",
          "declarații ce prezintă Holocaustul ca fiind un eveniment istoric pozitiv;",
          "încercări de a dilua responsabilitatea pentru înființarea de lagăre de concentrare și exterminare, concepute și operate de către Germania nazistă, prin învinovățirea altor țări sau grupuri etnice.",
        ],
      },
      {
        italic:
          "Holocaustul este persecuția sistematică sprijinită de stat și anihilarea evreilor europeni de către Germania nazistă, precum și de aliații și colaboratorii săi din perioada 1933-1945. De asemenea, în perioada celui de-al Doilea Război Mondial, o parte din populația romă a fost supusă deportării și anihilării.",
      },
      {
        italic:
          "Holocaustul pe teritoriul României este înțeles ca persecuția sistematică și anihilarea evreilor și a romilor, sprijinită de autoritățile și instituțiile statului român în teritoriile administrate de acesta în perioada 1940-1944.",
      },
    ],
  },
];

// Stil pentru marcajul „i" sau „ii" (roșu, bold, superscript) folosit
// atât în titluri, cât și ca prefix în body.
const markStyle = {
  color: "#FF0000",
  fontWeight: 700,
  fontSize: "0.7em",
  verticalAlign: "super",
  lineHeight: 1,
  whiteSpace: "nowrap",
};

// File upload validation rules
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
];
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif'];
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3 MB
const MAX_FILES = 10;

function Step2DetaliiSesizare({ formData, handleChange, handleDateChange, customColors, FormDataContent, files = [], setFiles, errors = {} }) {
  const handleSanitizedChange = (sanitize) => createSanitizedChangeHandler(handleChange, sanitize);

  const fileInputRef = useRef();
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [fileError, setFileError] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const dragDepthRef = useRef(0);

  // State pentru modalul cu Fraze explicative (stil identic cu „Biblioteca de informații")
  const [frazeOpen, setFrazeOpen] = useState(false);
  const [frazePosition, setFrazePosition] = useState({ top: 0, left: 0 });
  const [frazeWidth, setFrazeWidth] = useState(640);
  // Lățime maximă a modalului în funcție de breakpoint:
  // - desktop (>= 1024px): 720px (chenar mai mare pentru text mai mult)
  // - tabletă (>= 600px):  580px
  // - mobil (< 600px):     calc(100vw - 32px)
  const isDesktopFraze = useMediaQuery("(min-width:1024px)");
  const isTabletFraze = useMediaQuery("(min-width:600px)");

  const handleFrazeOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    let preferredWidth;
    if (isDesktopFraze) preferredWidth = 720;
    else if (isTabletFraze) preferredWidth = 580;
    else preferredWidth = window.innerWidth - 32;
    const tooltipWidth = Math.min(preferredWidth, window.innerWidth - 32);

    let left = rect.left + scrollX;
    let top = rect.bottom + scrollY + 8;

    if (rect.left + tooltipWidth > window.innerWidth - 16) {
      left = scrollX + window.innerWidth - tooltipWidth - 16;
    }
    if (left < scrollX + 16) {
      left = scrollX + 16;
    }

    setFrazePosition({ top, left });
    setFrazeWidth(tooltipWidth);
    setFrazeOpen(true);
  };

  const handleFrazeClose = () => setFrazeOpen(false);

  const handleFrazeKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleFrazeOpen(event);
    }
  };

  useEffect(() => {
    if (!frazeOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === "Escape") handleFrazeClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [frazeOpen]);

  // Fără asta, un fișier scăpat pe lângă zona de încărcare ar fi deschis de browser,
  // iar utilizatorul ar pierde tot ce a completat în formular.
  useEffect(() => {
    const blockFileDrop = (event) => {
      if (Array.from(event.dataTransfer?.types ?? []).includes("Files")) {
        event.preventDefault();
      }
    };
    window.addEventListener("dragover", blockFileDrop);
    window.addEventListener("drop", blockFileDrop);
    return () => {
      window.removeEventListener("dragover", blockFileDrop);
      window.removeEventListener("drop", blockFileDrop);
    };
  }, []);

  const handleTooltipOpen = (event, abbr) => {
    event.stopPropagation();
    setTooltipAnchor(event.currentTarget);
    setTooltipContent(abbreviationDefinitions[abbr] || abbr);
  };

  const handleTooltipClose = () => {
    setTooltipAnchor(null);
    setTooltipContent("");
  };

  // Open file dialog when SoftBox is clicked
  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Validare comună pentru fișierele venite din dialogul de selecție și din drag & drop
  const addFiles = (newFiles) => {
    setFileError("");

    if (newFiles.length === 0) return;

    // Check max files limit
    if (files.length + newFiles.length > MAX_FILES) {
      setFileError(`Poți încărca maximum ${MAX_FILES} fișiere.`);
      return;
    }

    const validFiles = [];
    const errorMessages = [];

    for (const file of newFiles) {
      // Check for duplicates
      if (files.some(f => f.name === file.name && f.size === file.size)) {
        continue; // Skip duplicates silently
      }

      // Check file type
      const extension = file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension)) {
        errorMessages.push(`"${file.name}" - tip de fișier neacceptat`);
        continue;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errorMessages.push(`"${file.name}" - depășește limita de 3 MB (${formatFileSize(file.size)})`);
        continue;
      }

      validFiles.push(file);
    }

    if (errorMessages.length > 0) {
      setFileError(errorMessages.join('; '));
    }

    if (validFiles.length > 0) {
      setFiles([...files, ...validFiles]);
    }
  };

  // Handle file selection with validation
  const handleFilesChange = (e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = null; // Reset input so same file can be selected again
  };

  // Ignorăm drag-urile care nu conțin fișiere (ex. selecție de text mutată cu mouse-ul)
  const dragCarriesFiles = (e) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

  const handleDragEnter = (e) => {
    if (!dragCarriesFiles(e)) return;
    e.preventDefault();
    // Numărăm enter/leave pentru că evenimentele se declanșează și la trecerea
    // peste elementele copil, iar zona ar clipi altfel.
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (e) => {
    if (!dragCarriesFiles(e)) return;
    e.preventDefault(); // fără asta browserul refuză drop-ul
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    if (!dragCarriesFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragActive(false);
  };

  const handleDrop = (e) => {
    if (!dragCarriesFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  // Remove file from list
  const handleRemoveFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  return (
    <SoftBox p={3}>
    <SoftTypography variant="h5" fontWeight="bold" mb={3} color={customColors.primary.main}>
      Detalii despre sesizare
    </SoftTypography>
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.subiect ? "error" : customColors.secondary.main}>
              Subiect*
            </SoftTypography>
          </SoftBox>
          <SoftInput 
            placeholder="Titlul scurt al sesizării" 
            name="subiect"
            value={formData.subiect}
            onChange={handleSanitizedChange(sanitizeSubiect)}
            required
            error={!!errors.subiect}
            inputProps={{ maxLength: FIELD_LIMITS.subiect }}
            icon={{
              component: "title",
              direction: "left"
            }}
          />
          {errors.subiect && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.subiect}
            </SoftTypography>
          )}
          <CharacterCounter value={formData.subiect} limit={FIELD_LIMITS.subiect} />
        </SoftBox>
      </Grid>
      <Grid item xs={12}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.descriere ? "error" : customColors.secondary.main}>
              Descriere detaliată*
            </SoftTypography>
          </SoftBox>
          <SoftInput 
            placeholder="Vă rugăm să oferiți cât mai multe detalii despre incident" 
            multiline
            rows={6}
            name="descriere"
            value={formData.descriere}
            onChange={handleSanitizedChange(sanitizeDescriere)}
            required
            error={!!errors.descriere}
            inputProps={{ maxLength: FIELD_LIMITS.descriere }}
            icon={{
              component: "description",
              direction: "left"
            }}
          />
          {errors.descriere && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.descriere}
            </SoftTypography>
          )}
          <CharacterCounter value={formData.descriere} limit={FIELD_LIMITS.descriere} />
        </SoftBox>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth error={!!errors.tipIncident}>
          <SoftBox mb={1} ml={0.5} display="flex" alignItems="center">
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.tipIncident ? "error" : customColors.secondary.main}>
              Tipul incidentului*
            </SoftTypography>
            {/* Iconița „i" pentru Fraze explicative – stil identic cu InfoTooltip din Biblioteca de informații */}
            <SoftBox
              component="span"
              role="button"
              tabIndex={0}
              aria-label="Fraze explicative pentru tipul incidentului"
              aria-haspopup="dialog"
              aria-expanded={frazeOpen}
              onClick={handleFrazeOpen}
              onKeyDown={handleFrazeKeyDown}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 14,
                height: 14,
                ml: 0.3,
                fontSize: "0.6rem",
                fontWeight: 600,
                fontStyle: "normal",
                color: "#1565C0",
                bgcolor: "rgba(21, 101, 192, 0.12)",
                borderRadius: "50%",
                cursor: "pointer",
                verticalAlign: "super",
                position: "relative",
                top: "-2px",
                lineHeight: 1,
                transition: "all 0.2s ease",
                border: "1px solid rgba(21, 101, 192, 0.25)",
                "&:hover": {
                  bgcolor: "rgba(21, 101, 192, 0.25)",
                  transform: "scale(1.15)",
                },
                "&:focus-visible": {
                  outline: "2px solid #1565C0",
                  outlineOffset: 2,
                },
              }}
            >
              i
            </SoftBox>
          </SoftBox>
          <Select
            value={formData.tipIncident}
            onChange={handleChange}
            name="tipIncident"
            displayEmpty
            required
            error={!!errors.tipIncident}
            sx={{ 
              height: '45px',
              '&.MuiOutlinedInput-root': {
                '& fieldset': { borderColor: errors.tipIncident ? '#f44336' : customColors.secondary.light },
                '&:hover fieldset': { borderColor: errors.tipIncident ? '#f44336' : customColors.secondary.main },
                '&.Mui-focused fieldset': { borderColor: errors.tipIncident ? '#f44336' : customColors.primary.main }
              }
            }}
            startAdornment={
              <Icon sx={{ mr: 1, color: customColors.secondary.main }}>category</Icon>
            }
          >
            <MenuItem value="" disabled>Selectați tipul incidentului</MenuItem>
            {FormDataContent && FormDataContent.incident_types && FormDataContent.incident_types.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
          {errors.tipIncident && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.tipIncident}
            </SoftTypography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl component="fieldset" error={!!errors.vatamare} fullWidth>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.vatamare ? "error" : customColors.secondary.main}>
              Tipul vătămării*
            </SoftTypography>
          </SoftBox>
          <SoftBox 
            sx={{ 
              pl: 0.5,
              '& .MuiFormControlLabel-root': {
                ml: 0,
                mb: 0.5
              }
            }}
          >
          <RadioGroup
            name="vatamare"
            value={formData.vatamare || ""}
            onChange={handleChange}
          >
            {FormDataContent && FormDataContent.injury_types && FormDataContent.injury_types.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={
                  <Radio
                    sx={{
                      color: errors.vatamare ? '#f44336' : customColors.primary.light,
                      '&.Mui-checked': { color: customColors.primary.main }
                    }}
                  />
                }
                label={item.label}
              />
            ))}
          </RadioGroup>
          </SoftBox>
          {errors.vatamare && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.vatamare}
            </SoftTypography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl component="fieldset" error={!!errors.responsabil} fullWidth>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.responsabil ? "error" : customColors.secondary.main}>
              Pe cine reclamați?*
            </SoftTypography>
          </SoftBox>
          <SoftBox 
            sx={{ 
              pl: 0.5,
              '& .MuiFormControlLabel-root': {
                ml: 0,
                mb: 0.5
              }
            }}
          >
          <RadioGroup
            name="responsabil"
            value={formData.responsabil}
            onChange={handleChange}
          >
              {FormDataContent && Array.isArray(FormDataContent.responsible_entity_types) && FormDataContent.responsible_entity_types.map((item) => {
                // Customize label - remove "de drept privat" from "Persoana Juridica de drept privat"
                let customLabel = item.label;
                if (item.label && item.label.toLowerCase().includes("de drept privat")) {
                  customLabel = item.label.replace(/\s*de drept privat/i, "");
                }
                return (
              <FormControlLabel
                key={item.value}
                value={item.value}
                    control={<Radio sx={{ color: errors.responsabil ? '#f44336' : customColors.primary.light, '&.Mui-checked': { color: customColors.primary.main } }} />}
                    label={customLabel}
                  />
                );
              })}
          </RadioGroup>
          </SoftBox>
          {errors.responsabil && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.responsabil}
            </SoftTypography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl component="fieldset" error={!!errors.locIncident} fullWidth>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.locIncident ? "error" : customColors.secondary.main}>
              Loc incident*
            </SoftTypography>
          </SoftBox>
          <SoftBox 
            sx={{ 
              pl: 0.5,
              '& .MuiFormControlLabel-root': {
                ml: 0,
                mb: 0.5
              }
            }}
          >
          <RadioGroup
            name="locIncident"
            value={formData.locIncident || ""}
            onChange={handleChange}
          >
            {FormDataContent && Array.isArray(FormDataContent.incident_place_types) && FormDataContent.incident_place_types.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={<Radio sx={{ color: errors.locIncident ? '#f44336' : customColors.primary.light, '&.Mui-checked': { color: customColors.primary.main } }} />}
                label={item.label}
              />
            ))}
          </RadioGroup>
          </SoftBox>
          {errors.locIncident && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.locIncident}
            </SoftTypography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.tipuriVatamareSuplimentar ? "error" : customColors.secondary.main}>
              Tipurile de vătămare
              <SoftTypography component="span" variant="caption" sx={{ color: "text.secondary", fontWeight: 400, ml: 0.5 }}>
                (opțional)
              </SoftTypography>
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Puteți detalia aici, dacă doriți"
            multiline
            rows={3}
            name="tipuriVatamareSuplimentar"
            value={formData.tipuriVatamareSuplimentar || ""}
            onChange={handleSanitizedChange(sanitizeTipuriVatamare)}
            error={!!errors.tipuriVatamareSuplimentar}
            inputProps={{ maxLength: FIELD_LIMITS.tipuriVatamareSuplimentar }}
            icon={{
              component: "notes",
              direction: "left",
            }}
          />
          {errors.tipuriVatamareSuplimentar && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.tipuriVatamareSuplimentar}
            </SoftTypography>
          )}
          <CharacterCounter
            value={formData.tipuriVatamareSuplimentar || ""}
            limit={FIELD_LIMITS.tipuriVatamareSuplimentar}
          />
          <SoftTypography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              ml: 0.5,
              color: "text.secondary",
              lineHeight: 1.55,
              maxWidth: "48rem",
            }}
          >
            Dacă incidentul v-a cauzat mai multe tipuri de vătămare, vă rugăm să le menționați și pe celelalte.
          </SoftTypography>
        </SoftBox>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl component="fieldset" error={!!errors.foreignSubmission} fullWidth>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.foreignSubmission ? "error" : customColors.secondary.main}>
              Ați mai sesizat anterior această situație?*
            </SoftTypography>
          </SoftBox>
          <SoftBox 
            sx={{ 
              pl: 0.5,
              '& .MuiFormControlLabel-root': {
                ml: 0,
                mb: 0.5
              }
            }}
          >
          <RadioGroup
            name="foreignSubmission"
            value={formData.foreignSubmission || ""}
            onChange={handleChange}
          >
            {FormDataContent && Array.isArray(FormDataContent.foreign_submission_choices) && FormDataContent.foreign_submission_choices.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={<Radio sx={{ color: errors.foreignSubmission ? '#f44336' : customColors.primary.light, '&.Mui-checked': { color: customColors.primary.main } }} />}
                label={item.label}
              />
            ))}
          </RadioGroup>
          </SoftBox>
          {errors.foreignSubmission && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.foreignSubmission}
            </SoftTypography>
          )}
        </FormControl>
      </Grid>
      {formData.foreignSubmission === "yes" && (
        <>
          <Grid item xs={12} md={4}>
            <FormControl component="fieldset" error={!!errors.foreignSubmissionResponsible} fullWidth>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.foreignSubmissionResponsible ? "error" : customColors.secondary.main}>
                Dacă ați sesizat anterior, unde ați depus sesizarea?*
              </SoftTypography>
            </SoftBox>
              <SoftBox 
                sx={{ 
                  pl: 0.5,
                  '& .MuiFormControlLabel-root': {
                    ml: 0,
                    mb: 0.5
                  }
                }}
              >
            <RadioGroup
              name="foreignSubmissionResponsible"
              value={formData.foreignSubmissionResponsible || ""}
              onChange={handleChange}
            >
              {FormDataContent && Array.isArray(FormDataContent.foreign_submission_responsible) && FormDataContent.foreign_submission_responsible.map((item) => {
                const isAbbreviation = abbreviationDefinitions[item.label];
                const labelShown = displayInstitutionLabel(item.label);
                return (
                  <FormControlLabel
                    key={item.value}
                    value={item.value}
                    control={<Radio sx={{ color: errors.foreignSubmissionResponsible ? '#f44336' : customColors.primary.light, '&.Mui-checked': { color: customColors.primary.main } }} />}
                    label={
                      isAbbreviation ? (
                        <SoftBox display="flex" alignItems="center" gap={0.5}>
                          {labelShown}
                          <SoftBox
                            component="span"
                            onClick={(e) => handleTooltipOpen(e, item.label)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "14px",
                              height: "14px",
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#1565C0",
                              cursor: "pointer",
                              position: "relative",
                              top: "-4px",
                              "&:hover": { color: "#0d47a1" }
                            }}
                          >
                            i
                          </SoftBox>
                        </SoftBox>
                      ) : item.label
                    }
                  />
                );
              })}
                  {/* Add "Altă Instituție" option (frontend only) */}
                  <FormControlLabel
                    value="other_institution"
                    control={<Radio sx={{ color: errors.foreignSubmissionResponsible ? '#f44336' : customColors.primary.light, '&.Mui-checked': { color: customColors.primary.main } }} />}
                    label="Altă Instituție"
                  />
            </RadioGroup>
              </SoftBox>
            {errors.foreignSubmissionResponsible && (
              <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                {errors.foreignSubmissionResponsible}
              </SoftTypography>
            )}
          </FormControl>
        </Grid>
          {/* Show text input when "Altă Instituție" is selected */}
          {formData.foreignSubmissionResponsible === "other_institution" && (
            <Grid item xs={12} md={4}>
              <SoftBox mb={2}>
                <SoftBox mb={1} ml={0.5}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.foreignSubmissionOtherInstitution ? "error" : customColors.secondary.main}>
                    Specificați instituția*
                  </SoftTypography>
                </SoftBox>
                <SoftInput 
                  placeholder="Introduceți numele instituției" 
                  name="foreignSubmissionOtherInstitution"
                  value={formData.foreignSubmissionOtherInstitution || ""}
                  onChange={handleChange}
                  required
                  error={!!errors.foreignSubmissionOtherInstitution}
                  icon={{
                    component: "business",
                    direction: "left"
                  }}
                />
                {errors.foreignSubmissionOtherInstitution && (
                  <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                    {errors.foreignSubmissionOtherInstitution}
                  </SoftTypography>
                )}
              </SoftBox>
            </Grid>
          )}
        </>
      )}
      <Grid item xs={12} md={4}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.dataFapte ? "error" : customColors.secondary.main}>
              Data faptelor*
            </SoftTypography>
          </SoftBox>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={roRO}>
            <DatePicker
              value={formData.dataFapte}
              onChange={handleDateChange}
              maxDate={new Date()}
              disableFuture
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  fullWidth 
                  error={!!errors.dataFapte}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: errors.dataFapte ? '#f44336' : customColors.secondary.light },
                      '&:hover fieldset': { borderColor: errors.dataFapte ? '#f44336' : customColors.secondary.main },
                      '&.Mui-focused fieldset': { borderColor: errors.dataFapte ? '#f44336' : customColors.primary.main }
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Icon sx={{ mr: 1, color: customColors.secondary.main }}>calendar_today</Icon>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </LocalizationProvider>
          {errors.dataFapte && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.dataFapte}
            </SoftTypography>
          )}
        </SoftBox>
      </Grid>
      {/* Locul unde s-a petrecut fapta - conditional based on locIncident */}
      {formData.locIncident === "online" && (
      <Grid item xs={12}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.locFapta ? "error" : customColors.secondary.main}>
                Link sau platformă online (unde s-a petrecut incidentul)*
              </SoftTypography>
            </SoftBox>
            <SoftInput 
              placeholder="Ex: URL website, platformă social media, nume platformă" 
              name="locFapta"
              value={formData.locFapta}
              onChange={handleChange}
              required
              error={!!errors.locFapta}
              icon={{
                component: "link",
                direction: "left"
              }}
            />
            {errors.locFapta && (
              <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                {errors.locFapta}
              </SoftTypography>
            )}
          </SoftBox>
        </Grid>
      )}
      
      {formData.locIncident === "offline" && (
        <>
          {/* Force new row for Țara, Județ, Oraș */}
          <Grid item xs={12} sx={{ height: 0, padding: '0 !important' }} />
          
          {/* Țara, Județul și Orașul - întotdeauna pe același rând pe desktop */}
          <Grid item xs={12} md={4}>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.tara ? "error" : customColors.secondary.main}>
                  Țara*
                </SoftTypography>
              </SoftBox>
              <FormControl fullWidth error={!!errors.tara}>
                <Select
                  value={formData.tara || "Romania"}
                  onChange={handleChange}
                  name="tara"
                  displayEmpty
                  required
                  sx={{ 
                    height: '45px',
                    '&.MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: errors.tara ? '#f44336' : customColors.secondary.light },
                      '&:hover fieldset': { borderColor: errors.tara ? '#f44336' : customColors.secondary.main },
                      '&.Mui-focused fieldset': { borderColor: errors.tara ? '#f44336' : customColors.primary.main }
                    }
                  }}
                  startAdornment={
                    <Icon sx={{ mr: 1, color: customColors.secondary.main }}>public</Icon>
                  }
                >
                  <MenuItem value="Romania">România</MenuItem>
                  <MenuItem value="Alta">Altă țară</MenuItem>
                </Select>
              </FormControl>
              {errors.tara && (
                <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                  {errors.tara}
                </SoftTypography>
              )}
            </SoftBox>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.judet ? "error" : customColors.secondary.main}>
                  Județul*
                </SoftTypography>
              </SoftBox>
              {formData.tara === "Romania" ? (
                <FormControl fullWidth error={!!errors.judet}>
                  <Select
                    value={formData.judet || ""}
                    onChange={handleChange}
                    name="judet"
                    displayEmpty
                    required
                    sx={{ 
                      height: '45px',
                      '&.MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: errors.judet ? '#f44336' : customColors.secondary.light },
                        '&:hover fieldset': { borderColor: errors.judet ? '#f44336' : customColors.secondary.main },
                        '&.Mui-focused fieldset': { borderColor: errors.judet ? '#f44336' : customColors.primary.main }
                      }
                    }}
                    startAdornment={
                      <Icon sx={{ mr: 1, color: customColors.secondary.main }}>location_city</Icon>
                    }
                  >
                    <MenuItem value="" disabled>Selectați județul</MenuItem>
                    {judeteRomania.map((judet) => (
                      <MenuItem key={judet} value={judet}>{judet}</MenuItem>
                    ))}
                  </Select>
                  {errors.judet && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.judet}
                    </SoftTypography>
                  )}
                </FormControl>
              ) : (
                <SoftBox 
                  sx={{ 
                    height: '45px', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: 'text.disabled',
                    fontStyle: 'italic'
                  }}
                >
                  <SoftTypography variant="button" color="text" sx={{ opacity: 0.5 }}>
                    -
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.oras ? "error" : customColors.secondary.main}>
                  Localitate*
                </SoftTypography>
              </SoftBox>
              {formData.tara === "Romania" ? (
                <>
                  <SoftInput 
                    placeholder="Ex: București, Cluj-Napoca, Brașov" 
                    name="oras"
                    value={formData.oras || ""}
                    onChange={handleSanitizedChange(sanitizeOras)}
                    required
                    error={!!errors.oras}
                    inputProps={{ maxLength: FIELD_LIMITS.oras }}
                    icon={{
                      component: "location_city",
                      direction: "left"
                    }}
                  />
                  {errors.oras && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.oras}
                    </SoftTypography>
                  )}
                  <CharacterCounter value={formData.oras || ""} limit={FIELD_LIMITS.oras} />
                </>
              ) : (
                <SoftBox 
                  sx={{ 
                    height: '45px', 
                    display: 'flex', 
                    alignItems: 'center',
                    color: 'text.disabled',
                    fontStyle: 'italic'
                  }}
                >
                  <SoftTypography variant="button" color="text" sx={{ opacity: 0.5 }}>
                    -
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
          </Grid>
          
          <Grid item xs={12}>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.locFapta ? "error" : customColors.secondary.main}>
                  {formData.tara === "Romania" ? "Adresa detaliată*" : "Locația (adresă sau descriere)*"}
            </SoftTypography>
          </SoftBox>
          <SoftInput 
                placeholder={formData.tara === "Romania" ? "Ex: Str. Exemplu nr. 10, Bloc A, Ap. 5" : "Descrieți locația unde s-a întâmplat incidentul"} 
                multiline
                rows={3}
            name="locFapta"
            value={formData.locFapta}
            onChange={handleChange}
            required
            error={!!errors.locFapta}
            icon={{
              component: "place",
              direction: "left"
            }}
          />
          <SoftTypography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              ml: 0.5,
              color: "text.secondary",
              lineHeight: 1.55,
              maxWidth: "52rem",
            }}
          >
            Puteți include în acest câmp și un link obținut prin funcția „Partajează locația” din aplicația de hărți (ex. Google Maps, Apple Maps), alături de adresă sau ca supliment la descriere.
          </SoftTypography>
          {errors.locFapta && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {errors.locFapta}
            </SoftTypography>
          )}
        </SoftBox>
      </Grid>
        </>
      )}
      <Grid item xs={12}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold" color={(errors.files || fileError) ? "error" : customColors.secondary.main}>
              Atașamente (dovezi) (opțional)
            </SoftTypography>
          </SoftBox>
          <SoftBox
            p={3}
            border={(isDragActive || errors.files || fileError) ? "2px dashed" : "1px dashed"}
            borderColor={
              isDragActive
                ? customColors.primary.main
                : (errors.files || fileError) ? "#f44336" : customColors.primary.light
            }
            borderRadius="lg"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              backgroundColor: isDragActive
                ? customColors.primary.focus
                : (errors.files || fileError) ? '#ffebee' : 'transparent',
              '&:hover': { backgroundColor: (errors.files || fileError) ? '#ffcdd2' : customColors.primary.focus }
            }}
            onClick={handleBoxClick}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            position="relative"
          >
            <Icon fontSize="large" sx={{ color: (errors.files || fileError) && !isDragActive ? "#f44336" : customColors.primary.main }}>
              {isDragActive ? "file_download" : "cloud_upload"}
            </Icon>
            <SoftTypography variant="button" fontWeight="regular" mt={1} color={(errors.files || fileError) && !isDragActive ? "error" : customColors.primary.main}>
              {isDragActive
                ? "Eliberați fișierele pentru a le încărca"
                : "Trageți fișierele aici sau click pentru a încărca"}
            </SoftTypography>
            <SoftTypography variant="caption" color="text" sx={{ mt: 1, opacity: 0.7, textAlign: 'center' }}>
              PDF, imagini (JPG, PNG, GIF, WEBP, BMP, TIFF) • Max. 3 MB/fișier • Max. 10 fișiere
            </SoftTypography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.tif,application/pdf,image/*"
              style={{ display: "none" }}
              onChange={handleFilesChange}
              tabIndex={-1}
            />
          </SoftBox>
          {/* File validation error */}
          {fileError && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 0.5 }}>
              <Icon sx={{ fontSize: '0.9rem !important', verticalAlign: 'middle', mr: 0.5 }}>warning</Icon>
              {fileError}
            </SoftTypography>
          )}
          {/* List selected files */}
          {files && files.length > 0 && (
            <SoftBox mt={2}>
              <SoftTypography variant="caption" color="text" sx={{ mb: 1, display: 'block' }}>
                {files.length} din {MAX_FILES} fișiere selectate
              </SoftTypography>
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {files.map((file, idx) => (
                  <li key={file.name + file.size} style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                    <Icon sx={{ fontSize: '1rem !important', mr: 0.5, color: '#1565C0' }}>attach_file</Icon>
                    <span style={{ marginRight: 8, flex: 1 }}>
                      {file.name}
                      <span style={{ color: '#666', fontSize: '0.75rem', marginLeft: 8 }}>
                        ({formatFileSize(file.size)})
                      </span>
                    </span>
                    <Icon
                      fontSize="small"
                      color="error"
                      sx={{ cursor: "pointer", '&:hover': { opacity: 0.7 } }}
                      onClick={() => handleRemoveFile(idx)}
                    >
                      delete
                    </Icon>
                  </li>
                ))}
              </ul>
            </SoftBox>
          )}
          {errors.files && (
            <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 0.5 }}>
              {errors.files}
            </SoftTypography>
          )}
          {!errors.files && (
            <SoftTypography variant="caption" color="text" sx={{ display: 'block', mt: 1 }}>
              Puteți atașa documente sau fotografii ca dovezi
            </SoftTypography>
          )}
        </SoftBox>
      </Grid>
    </Grid>
    {/* Popover for abbreviation tooltips */}
    <Popover
      open={Boolean(tooltipAnchor)}
      anchorEl={tooltipAnchor}
      onClose={handleTooltipClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        zIndex: 9999,
        '& .MuiPopover-paper': {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          maxWidth: '280px',
          background: 'linear-gradient(135deg, #1565C0, #0d47a1)',
        }
      }}
    >
      <SoftBox sx={{ padding: '10px 14px' }}>
        <SoftTypography sx={{ color: '#ffffff', fontSize: '0.82rem', lineHeight: 1.5, fontWeight: 500 }}>
          {tooltipContent}
        </SoftTypography>
      </SoftBox>
    </Popover>

    {/* Overlay Fraze explicative – stil identic cu TooltipOverlay din Biblioteca de informații */}
    {frazeOpen && createPortal(
      <>
        {/* Backdrop pentru a închide la click în afara box-ului */}
        <div
          onClick={handleFrazeClose}
          aria-hidden="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483646,
          }}
        />
        {/* Conținut tooltip */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="fraze-explicative-title"
          style={{
            position: "absolute",
            top: frazePosition.top,
            left: frazePosition.left,
            width: frazeWidth,
            maxWidth: "calc(100vw - 32px)",
            backgroundColor: "white",
            borderRadius: "10px",
            boxShadow: "0 12px 48px rgba(0,0,0,0.35)",
            border: "1px solid rgba(21, 101, 192, 0.2)",
            overflow: "hidden",
            zIndex: 2147483647,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "12px 16px",
              background: "linear-gradient(135deg, #1565C0, #0d47a1)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span id="fraze-explicative-title" style={{ color: "#ffffff", fontSize: "0.85rem", fontWeight: 700 }}>
              Fraze explicative
            </span>
            <button
              type="button"
              onClick={handleFrazeClose}
              aria-label="Închide informația"
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.8)",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
              }}
            >
              <Icon sx={{ fontSize: "1rem !important", color: "inherit" }}>close</Icon>
            </button>
          </div>
          {/* Content – scrollabil */}
          <div style={{ padding: "16px", maxHeight: "min(70vh, 560px)", overflowY: "auto" }}>
            {frazeExplicative.map((fraza, idx) => (
              <div
                key={fraza.id}
                style={{
                  marginTop: idx === 0 ? 0 : 18,
                  paddingTop: idx === 0 ? 0 : 14,
                  borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.08)",
                }}
              >
                {/* Titlul cu marcaje „i"/„ii" roșii intercalate */}
                <p style={{
                  margin: 0,
                  marginBottom: 8,
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  color: "#1565C0",
                  lineHeight: 1.4,
                }}>
                  {fraza.title.map((seg, i) => (
                    seg.mark
                      ? <span key={i} style={markStyle}>{seg.mark}</span>
                      : <span key={i}>{seg.text}</span>
                  ))}
                </p>

                {/* Body: paragrafe italic, paragrafe cu prefix „i"/„ii" sau liste */}
                {fraza.body.map((item, i) => {
                  if (item.list) {
                    return (
                      <ul
                        key={i}
                        style={{
                          margin: "8px 0 0 0",
                          paddingLeft: 20,
                          color: "#344767",
                          fontSize: "0.85rem",
                          lineHeight: 1.7,
                          fontStyle: "italic",
                        }}
                      >
                        {item.list.map((li, j) => (
                          <li key={j} style={{ marginBottom: 4 }}>{li}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (item.italicSegments) {
                    return (
                      <p
                        key={i}
                        style={{
                          margin: "8px 0 0 0",
                          color: "#344767",
                          fontSize: "0.85rem",
                          lineHeight: 1.7,
                          fontStyle: "italic",
                        }}
                      >
                        {item.italicSegments.map((seg, j) => (
                          seg.bold
                            ? <strong key={j}>{seg.text}</strong>
                            : <span key={j}>{seg.text}</span>
                        ))}
                      </p>
                    );
                  }
                  return (
                    <p
                      key={i}
                      style={{
                        margin: "8px 0 0 0",
                        color: "#344767",
                        fontSize: "0.85rem",
                        lineHeight: 1.7,
                        fontStyle: "italic",
                      }}
                    >
                      {item.mark && (
                        <>
                          <span style={markStyle}>{item.mark}</span>
                          {" "}
                        </>
                      )}
                      {item.italic}
                    </p>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </>,
      document.body
    )}
  </SoftBox>
  );
}

Step2DetaliiSesizare.propTypes = {
  formData: PropTypes.shape({
    subiect: PropTypes.string,
    descriere: PropTypes.string,
    tipIncident: PropTypes.string,
    vatamare: PropTypes.string,
    responsabil: PropTypes.string,
    locIncident: PropTypes.string,
    foreignSubmission: PropTypes.string,
    foreignSubmissionResponsible: PropTypes.string,
    foreignSubmissionOtherInstitution: PropTypes.string,
    dataFapte: PropTypes.any,
    tara: PropTypes.string,
    judet: PropTypes.string,
    oras: PropTypes.string,
    locFapta: PropTypes.string,
    tipuriVatamareSuplimentar: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  customColors: PropTypes.shape({
    primary: PropTypes.shape({
      light: PropTypes.string,
      focus: PropTypes.string,
      main: PropTypes.string
    }),
    secondary: PropTypes.shape({
      main: PropTypes.string,
      light: PropTypes.string
    })
  }).isRequired,
  FormDataContent: PropTypes.any,
  files: PropTypes.array,
  setFiles: PropTypes.func,
  errors: PropTypes.object,
};

export default Step2DetaliiSesizare;
